import React, {memo, useCallback, useRef} from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {alpha, SxProps, Theme} from '@mui/material/styles';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import {C84_PAGE} from '@/pages/components/audit-program/c-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface C84RiskResponseSummaryViewProps {
    header: AuditProgramSectionVO;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

const FIELDS = ['risks', 'residual', 'justification', 'approach'] as const;

const cellBorder = {borderColor: 'divider'};
const headSx = {
    ...cellBorder,
    fontWeight: 600,
    fontSize: '0.7rem',
    py: 0.75,
    px: 0.4,
    lineHeight: 1.25,
    verticalAlign: 'bottom' as const,
    whiteSpace: 'pre-line' as const,
};
const bodySx = {
    ...cellBorder,
    py: 0.4,
    px: 0.4,
    verticalAlign: 'top' as const,
    overflow: 'hidden',
};

const nativeBaseSx: SxProps<Theme> = {
    display: 'block',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    m: 0,
    border: '1px solid',
    borderColor: 'rgba(0, 0, 0, 0.23)',
    borderRadius: 1,
    px: 0.75,
    py: '4px',
    fontSize: (theme) => theme.typography.body2.fontSize,
    lineHeight: 1.25,
    fontFamily: 'inherit',
    color: 'text.primary',
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
    outline: 'none',
    '&:hover': {borderColor: 'text.primary'},
    '&:focus': {
        borderColor: 'primary.main',
        boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
    },
};

const CellInput = memo(function CellInput({
    value,
    onChange,
    multiline,
    align = 'left',
    rows = 2,
}: {
    value: string;
    onChange: (v: string) => void;
    multiline?: boolean;
    align?: 'left' | 'center';
    rows?: number;
}) {
    if (multiline) {
        return (
            <Box
                component="textarea"
                value={value}
                rows={rows}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
                sx={{...nativeBaseSx, resize: 'vertical', textAlign: align}}
            />
        );
    }
    return (
        <Box
            component="input"
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
            sx={{...nativeBaseSx, textAlign: align}}
        />
    );
});

const DataRow = memo(function DataRow({
    id,
    code,
    areaLabel,
    indent,
    risks,
    residual,
    justification,
    approach,
    onChange,
}: {
    id: string;
    code: string;
    areaLabel: string;
    indent?: boolean;
    risks: string;
    residual: string;
    justification: string;
    approach: string;
    onChange: (key: string, value: string) => void;
}) {
    const set = useCallback(
        (field: (typeof FIELDS)[number]) => (v: string) => onChange(`${id}.${field}`, v),
        [id, onChange],
    );

    return (
        <TableRow>
            <TableCell sx={bodySx} align="center">
                <Typography variant="body2" fontWeight={600}>{code}</Typography>
            </TableCell>
            <TableCell sx={bodySx}>
                <Typography variant="body2" sx={{lineHeight: 1.35, pl: indent ? 1.5 : 0}}>
                    {areaLabel}
                </Typography>
            </TableCell>
            <TableCell sx={bodySx}>
                <CellInput value={risks} onChange={set('risks')} multiline rows={2}/>
            </TableCell>
            <TableCell sx={bodySx}>
                <CellInput value={residual} onChange={set('residual')} align="center"/>
            </TableCell>
            <TableCell sx={bodySx}>
                <CellInput value={justification} onChange={set('justification')} multiline rows={2}/>
            </TableCell>
            <TableCell sx={bodySx}>
                <CellInput value={approach} onChange={set('approach')} multiline rows={2}/>
            </TableCell>
        </TableRow>
    );
});

/** Excel C8.4 — Risk Response Summary */
export default function C84RiskResponseSummaryView({
    header,
    values,
    onChange,
}: C84RiskResponseSummaryViewProps) {
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const stableOnChange = useCallback((key: string, value: string) => {
        onChangeRef.current(key, value);
    }, []);

    const f = (id: string, field: (typeof FIELDS)[number]) => values[`${id}.${field}`] ?? '';
    const matrix = C84_PAGE.riskMatrix;

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader header={header} variant="a13" refCode="C8.4" values={values} onChange={stableOnChange}/>

            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 1.5,
                    mt: 0.5,
                }}
            >
                <Typography variant="subtitle1" fontWeight={700} sx={{letterSpacing: 0.4}}>
                    {C84_PAGE.title}
                </Typography>

                <Box sx={{minWidth: 220}}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={0.5}>
                        {matrix.title}
                    </Typography>
                    <Table size="small" sx={{borderCollapse: 'collapse', width: 'auto'}}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{...headSx, fontSize: '0.65rem'}}>{matrix.fsLabel}</TableCell>
                                {matrix.cols.map((c) => (
                                    <TableCell key={c} sx={{...headSx, fontSize: '0.65rem'}} align="center">
                                        {c}
                                    </TableCell>
                                ))}
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{...headSx, fontSize: '0.65rem'}} colSpan={4} align="right">
                                    {matrix.assertionLabel}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {matrix.rows.map((row) => (
                                <TableRow key={row.level}>
                                    <TableCell sx={{...bodySx, fontWeight: 600}} align="center">{row.level}</TableCell>
                                    {row.values.map((v, i) => (
                                        <TableCell key={i} sx={bodySx} align="center">
                                            <Typography variant="caption">{v}</Typography>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            </Box>

            <TableContainer sx={{overflowX: 'auto'}}>
                <Table
                    size="small"
                    sx={{
                        width: '100%',
                        minWidth: 1100,
                        tableLayout: 'fixed',
                        borderCollapse: 'collapse',
                    }}
                >
                    <colgroup>
                        <col style={{width: '4%'}}/>
                        <col style={{width: '18%'}}/>
                        <col style={{width: '18%'}}/>
                        <col style={{width: '8%'}}/>
                        <col style={{width: '26%'}}/>
                        <col style={{width: '26%'}}/>
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx} align="center">{C84_PAGE.columns.code}</TableCell>
                            <TableCell sx={headSx}>{C84_PAGE.columns.area}</TableCell>
                            <TableCell sx={headSx}>{C84_PAGE.columns.risks}</TableCell>
                            <TableCell sx={headSx} align="center">{C84_PAGE.columns.residual}</TableCell>
                            <TableCell sx={headSx}>{C84_PAGE.columns.justification}</TableCell>
                            <TableCell sx={headSx}>{C84_PAGE.columns.approach}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {C84_PAGE.rows.map((row) => {
                            const areaLabel = row.sub
                                ? (row.name ? `${row.name} — ${row.sub}` : row.sub)
                                : row.name;
                            return (
                                <DataRow
                                    key={row.id}
                                    id={row.id}
                                    code={row.code}
                                    areaLabel={areaLabel}
                                    indent={row.kind === 'sub'}
                                    risks={f(row.id, 'risks')}
                                    residual={f(row.id, 'residual')}
                                    justification={f(row.id, 'justification')}
                                    approach={f(row.id, 'approach')}
                                    onChange={stableOnChange}
                                />
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 1.5, lineHeight: 1.4}}>
                {C84_PAGE.note}
            </Typography>
        </Box>
    );
}
